/**
 * Store pour la gestion des commentaires des publications sur les réseaux sociaux
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

import { useManagingUser } from '@compositions/ManagingUser';

export const useSocialPublicationCommentStore = defineStore('socialPublicationComment', (() => {

    // Composition pour l'utilisateur que l'on gère
    const { getManagingUser } = useManagingUser({});

    // State des données de la liste des commentaires groupé par identifiant des clients
    const commentsData = ref({});

    /**
     * Retourne la liste des commentaires de l'utilisateur que l'on gère
     * @return Object
     */
    const getCommentsList = computed(() => {
        const userId = getManagingUser.value.id;
        const userCommentsData = (commentsData.value[userId] ?? {});
        return (userCommentsData.list ?? {});
    });

    /**
     * Chargement des commentaires des publications de l'utilisateur que l'on gère
     * @param Object params
     * @return Promise
     */
    const loadComments = ((params) => {

        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/publications/social-network-comments`;

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {
                if(params.offset === 0) {
                    resetCommentsList();
                }

                updateCommentsList(response.data.data);

                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Chargement des données du commentaire dont l'identifiant est en paramètre
     * @param int commentId
     * @return Promise
     */
    const loadComment = ((commentId) => {
        const userId = getManagingUser.value.id;

        const path = `/api/users/${ userId }/publications/social-network-comments/${ commentId }`;

        return axios.get(path).then((response) => {
            const success = (response?.data?.success ?? false);
            const commentData = (response?.data?.comment ?? null);
            if(success && commentData !== null) {
                return Promise.resolve(commentData);
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie la liste des commentaires de l'utilisateur que l'on gère
     * @param data Données des commentaires : total et items
     * @return void
     */
    const updateCommentsList = ((data) => {

        const userId = getManagingUser.value.id;

        commentsData.value[userId] ??= {
            list: {},
            comments: []
        };

        const currentData = commentsData.value[userId];

        if(currentData.list.total === undefined) {
            commentsData.value[userId].list = {
                total: data.total,
                items: data.items
            };
            commentsData.value[userId].comments = data.items;
        } else {
            commentsData.value[userId].list.total = data.total;

            const currentItems = commentsData.value[userId].list.items;
            const newItems = data.items.filter((item) => ! currentItems.find((current) => current.id === item.id));
            commentsData.value[userId].list.items = [ ...currentItems, ...newItems ];

            newItems.forEach((item) => {
                updateComment(item);
            });
        }

    });

    /**
     * Supprime un commentaire du store
     * @param object comment
     * @return void
     */
    const removeCommentFromStore = ((comment) => {

        const userId = getManagingUser.value.id;

        if(! commentsData.value[userId]) {
            return;
        }

        const commentIndexFromList = commentsData.value[userId].list.items.findIndex((current) => current.id === comment.id);
        if(commentIndexFromList !== -1) {
            commentsData.value[userId].list.items.splice(commentIndexFromList, 1);
            commentsData.value[userId].list.total -= 1;
        }

        const commentIndex = commentsData.value[userId].comments.findIndex((current) => current.id === comment.id);
        if(commentIndex !== -1) {
            commentsData.value[userId].comments.splice(commentIndex, 1);
        }

    });

    /**
     * Met à jour le commentaire en paramètre dans le store
     * @param Object comment
     * @return void
     */
    const updateComment = ((comment, canAddComment = true) => {

        const targetItemId = comment.id;

        const userId = getManagingUser.value.id;

        commentsData.value[userId] ??= {
            list: {},
            comments: {}
        }


        const index = commentsData.value[userId].comments.findIndex((itemFromState) => itemFromState.id === targetItemId);
        if(index !== -1) {
            commentsData.value[userId].comments[index] = {
                ...commentsData.value[userId].comments[index],
                ...comment
            };
        } else if(canAddComment) {
            commentsData.value[userId].comments.push(comment);
        }

        // Synchronisation avec les éléments de la liste
        const listData = commentsData.value[userId].list;
        if(listData.total !== undefined) {
            const listItems = listData.items || [];
            const indexInList = listItems.findIndex((itemFromState) => itemFromState.id === targetItemId);
            if(indexInList !== -1) {
                listItems[indexInList] = { ...listItems[indexInList], ...comment };
            } else {
                commentsData.value[userId].list.total += 1;
                listItems.push(comment);
            }

            commentsData.value[userId].list.items = listItems;
        }

    });

    /**
     * Marque le commentaire en paramètre comme lu
     * @param object comment
     * @return Promise
     */
    const markCommentAsRead = ((comment) => {

        const ownerId = getManagingUser.value.id;
        const publicationId = comment.publicationId;
        const commentId = comment.id;
        const apiPath = `/api/users/${ ownerId }/publications/${ publicationId }/social-network-comments/${ commentId }/mark-as-read`;

        return axios.post(apiPath).then((response) => {
            const success = (response?.data?.success ?? false);
            const newComment = (response?.data?.comment ?? null);

            if(! success || newComment === null) {
                return Promise.reject();
            }

            updateComment(newComment, false);
            return Promise.resolve(response.data.comment);
        });
    });

    /**
     * Réinitialisation de la liste des commentaires de l'utilisateur que l'on gère
     * @return void
     */
    const resetCommentsList = (() => {
        const userId = getManagingUser.value.id;
        delete commentsData.value[userId];
    });

    /**
     * Réinitialisation complète du store
     * @return void
     */
    const reset = (() => {
        commentsData.value = {};
    });

    return {
        getCommentsList,
        loadComments,
        loadComment,
        updateComment,
        markCommentAsRead,
        resetCommentsList,
        reset,
        removeCommentFromStore
    };
}));


